import { Navigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { COMPANY_TYPE } from '@constants/app';
import withChatbotLayout from '@product-framework/components/Chatbot/withChatbotLayout';
import RouteRBACCheck from './RouteRBACCheck';
import AppDataProvider from '../../providers/AppDataProvider';
import { useSnackbar } from '../../providers/SnackbarProvider';
import LoadingEvent from '../UI/Skeleton/LoadingEvent';
import SnackbarApiErrorMessage from '../UI/SnackbarApiErrorMessage';
import { useDataProvider } from '../../providers/DataProvider';

function ProtectedRoute({
  componentProps = {},
  Component,
  ErrorPage,
  ERROR_PAGE_TYPE,
  AuthorizationWrapper = null,
  AuthorizationWrapperProps = {},
}) {
  const {
    isLoading = false,
    error,
    isAuthenticated,
  } = useAuth() || {
    isLoading: false,
    isAuthenticated: false,
    error: 'Auth Error',
  };
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const getInjectableAdditionalMetaData = async userAccess => {
    const getCountryByCode = async countryCode => {
      const url = `${getApiUrl()}/sxcl/v1/countries?countryCode=${countryCode}&limit=1`;
      const response = await get(url);
      if (response?.ok) {
        return {
          ...response?.data?.data?.[0],
          label: response?.data?.data?.[0]?.countryName,
          value: response?.data?.data?.[0]?.id,
        };
      }
      return null;
    };

    const fetchSP = async spId => {
      const url = `${getApiUrl()}/sxcl/v1/service-providers/${spId}`;
      const response = await get(url);
      if (response?.ok) {
        return response.data;
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
      return null;
    };

    const getUserProfile = async () => {
      let userProfile = {};
      if (userAccess?.company?.companyType === COMPANY_TYPE.SERVICE_PROVIDER) {
        const spDetails = await fetchSP(userAccess.company.id);
        if (spDetails) {
          userProfile = spDetails;
        }
      }
      return userProfile;
    };

    const countryUS = await getCountryByCode('US');
    const userProfile = await getUserProfile();

    return {
      staticCountry: countryUS,
      loggedInUser: userProfile,
    };
  };

  if (error) {
    return (
      <ErrorPage
        redirectUri={window.location.href}
        type={ERROR_PAGE_TYPE.AUTH_ERROR}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <RouteRBACCheck>
        {AuthorizationWrapper ? (
          <AuthorizationWrapper {...AuthorizationWrapperProps}>
            <AppDataProvider
              getInjectableAdditionalMetaData={getInjectableAdditionalMetaData}
              metaDataUrl="/sxcl/v1/config">
              <Component {...componentProps} />
            </AppDataProvider>
          </AuthorizationWrapper>
        ) : (
          <AppDataProvider
            getInjectableAdditionalMetaData={getInjectableAdditionalMetaData}
            metaDataUrl="/sxcl/v1/config">
            <Component {...componentProps} />
          </AppDataProvider>
        )}
      </RouteRBACCheck>
    );
  }

  if (isLoading) {
    return (
      <LoadingEvent>
        <FormattedMessage
          id="AUTHENTICATING"
          defaultMessage="Authenticating..."
        />
      </LoadingEvent>
    );
  }

  return (
    <Navigate
      state={{ redirect_uri: window.location.href }}
      to={{
        pathname: '/unauthorized',
      }}
    />
  );
}

export default withChatbotLayout(ProtectedRoute);
